/**
 * Enhanced Predictive Search with fuzzy matching and author results.
 *
 * Loads a static search index (search-index.json) on first focus, then
 * performs client-side bigram-similarity search that handles:
 *   - Fuzzy/typo matching ("waitely" → "Waitley")
 *   - Out-of-order words ("rohn jim" → "Jim Rohn")
 *   - Author matching alongside products
 *
 * Falls back to Shopify's Predictive Search API while the index loads.
 */
class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.cachedResults = {};
    this.input = this.querySelector('input[type="search"]');
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.isOpen = false;

    this.searchIndex = null;
    this.indexLoading = false;
    this.indexUrl = this.getAttribute('data-index-url');

    this.setupEventListeners();
  }

  setupEventListeners() {
    const form = this.querySelector('form.search');
    form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('input', debounce((event) => {
      this.onChange(event);
    }, 300).bind(this));
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    const searchTerm = this.getQuery();

    if (!searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFocus() {
    this.loadIndex();

    const searchTerm = this.getQuery();

    if (!searchTerm.length) return;

    if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    })
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up')
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    if (
      event.code === 'ArrowUp' ||
      event.code === 'ArrowDown'
    ) {
      event.preventDefault();
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');
    const allElements = this.querySelectorAll('li');
    let activeElement = this.querySelector('li');

    if (moveUp && !selectedElement) return;

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElement = selectedElement.nextElementSibling || allElements[0];
    } else if (moveUp) {
      activeElement = selectedElement.previousElementSibling || allElements[allElements.length - 1];
    }

    if (activeElement === selectedElement) return;

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.setLiveRegionText(activeElement.textContent);
    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedProduct = this.querySelector('[aria-selected="true"] a, [aria-selected="true"] button');

    if (selectedProduct) selectedProduct.click();
  }

  /* ── Index loading ────────────────────────────────────── */

  loadIndex() {
    if (this.searchIndex || this.indexLoading || !this.indexUrl) return;
    this.indexLoading = true;

    fetch(this.indexUrl)
      .then(r => r.json())
      .then(data => {
        this.searchIndex = data;
        this.indexLoading = false;

        // Pre-compute bigrams for fast matching
        for (const p of this.searchIndex.products) {
          p._bg = this._bigrams(`${p.t} ${p.a || ''}`);
          p._tBg = this._bigrams(p.t);
          p._aBg = this._bigrams(p.a || '');
          p._lower = `${p.t} ${p.a || ''}`.toLowerCase();
        }
        for (const a of this.searchIndex.authors) {
          a._bg = this._bigrams(a.n);
          a._lower = a.n.toLowerCase();
          if (a.al && a.al.length) {
            a._alBg = a.al.map(s => this._bigrams(s));
            a._alLower = a.al.map(s => s.toLowerCase());
          }
        }
        // Articles — added per PLAN_ARTICLE_MIGRATION.md Chunk G. Backward-
        // compatible: older builds of search-index.json may not have the
        // articles array.
        if (this.searchIndex.articles) {
          for (const art of this.searchIndex.articles) {
            art._bg = this._bigrams(`${art.t} ${art.a || ''} ${art.e || ''}`);
            art._tBg = this._bigrams(art.t);
            art._aBg = this._bigrams(art.a || '');
            art._lower = `${art.t} ${art.a || ''} ${art.e || ''}`.toLowerCase();
          }
        }

        // If user typed while index was loading, re-run their query
        const pending = this.getQuery();
        if (pending.length) this.getSearchResults(pending);
      })
      .catch(() => { this.indexLoading = false; });
  }

  /* ── Bigram fuzzy matching ────────────────────────────── */

  _bigrams(str) {
    const s = str.toLowerCase().replace(/[^a-z0-9 ]/g, '');
    const set = new Set();
    for (let i = 0; i < s.length - 1; i++) set.add(s[i] + s[i + 1]);
    return set;
  }

  _bigramScore(a, b) {
    if (!a.size || !b.size) return 0;
    let hits = 0;
    for (const p of a) { if (b.has(p)) hits++; }
    return (2 * hits) / (a.size + b.size);
  }

  _fuzzySearch(query) {
    const q = query.toLowerCase().replace(/[^a-z0-9 ]/g, '');
    if (!q) return { products: [], authors: [] };

    const qBg = this._bigrams(q);
    const words = q.split(/\s+/).filter(w => w.length > 0);
    const wordBigrams = words.map(w => this._bigrams(w));

    const scoreItem = (itemBg, itemLower, titleBg, authorBg) => {
      let score = this._bigramScore(qBg, itemBg);

      // Substring bonus
      if (itemLower.includes(q)) score += 0.5;

      // Multi-word: score each word against title/author independently
      if (words.length > 1 && titleBg) {
        let ws = 0;
        for (let i = 0; i < words.length; i++) {
          const tScore = this._bigramScore(wordBigrams[i], titleBg);
          const aScore = authorBg ? this._bigramScore(wordBigrams[i], authorBg) : 0;
          ws += Math.max(tScore, aScore);
          if (itemLower.includes(words[i])) ws += 0.3;
        }
        score = Math.max(score, ws / words.length);
      } else if (words.length === 1) {
        // Single word prefix bonus
        if (itemLower.startsWith(q)) score += 0.4;
        else if (itemLower.includes(' ' + q)) score += 0.3;
      }

      return score;
    };

    // Score products
    const products = [];
    for (const p of this.searchIndex.products) {
      const score = scoreItem(p._bg, p._lower, p._tBg, p._aBg);
      if (score > 0.2) products.push({ ...p, _score: score });
    }

    // Score authors
    const authors = [];
    for (const a of this.searchIndex.authors) {
      let score = scoreItem(a._bg, a._lower, a._bg, null);

      // Check aliases
      if (a._alBg) {
        for (let i = 0; i < a._alBg.length; i++) {
          const as = this._bigramScore(qBg, a._alBg[i]);
          const bonus = a._alLower[i].includes(q) ? 0.5 : 0;
          score = Math.max(score, as + bonus);
        }
      }

      // Multi-word for author names
      if (words.length > 1) {
        let ws = 0;
        for (let i = 0; i < words.length; i++) {
          ws += this._bigramScore(wordBigrams[i], a._bg);
          if (a._lower.includes(words[i])) ws += 0.3;
        }
        score = Math.max(score, ws / words.length);
      }

      if (score > 0.2) authors.push({ ...a, _score: score });
    }

    // Score articles
    const articles = [];
    if (this.searchIndex.articles) {
      for (const art of this.searchIndex.articles) {
        const score = scoreItem(art._bg, art._lower, art._tBg, art._aBg);
        if (score > 0.2) articles.push({ ...art, _score: score });
      }
    }

    products.sort((a, b) => b._score - a._score);
    authors.sort((a, b) => b._score - a._score);
    articles.sort((a, b) => b._score - a._score);

    return {
      products: products.slice(0, 5),
      authors: authors.slice(0, 3),
      articles: articles.slice(0, 3),
    };
  }

  /* ── HTML rendering ───────────────────────────────────── */

  _escapeHTML(str) {
    const d = document.createElement('div');
    d.textContent = str;
    return d.innerHTML;
  }

  _buildResultsHTML(results, searchTerm) {
    const esc = s => this._escapeHTML(s);
    let idx = 0;
    let html = '<div id="predictive-search-results">';

    // Author section
    if (results.authors.length) {
      html += '<h2 class="predictive-search__heading text-body caption-with-letter-spacing">Authors</h2>';
      html += '<ul class="predictive-search__results-list list-unstyled" role="listbox" aria-labelledby="predictive-search-authors">';
      for (const a of results.authors) {
        idx++;
        const imgHtml = a.i
          ? `<img class="predictive-search__image predictive-search__image--round" src="${esc(a.i)}" alt="${esc(a.n)}" width="50" height="50">`
          : `<div class="predictive-search__image predictive-search__author-icon" aria-hidden="true"><svg viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="currentColor" stroke-width="1.5"><circle cx="12" cy="8" r="4"/><path d="M4 21v-1a8 8 0 0116 0v1"/></svg></div>`;
        html += `<li id="predictive-search-option-${idx}" class="predictive-search__list-item" role="option" aria-selected="false">
          <a href="/pages/${esc(a.h)}" class="predictive-search__item predictive-search__item--link link link--text" tabindex="-1">
            ${imgHtml}
            <div class="predictive-search__item-content predictive-search__item-content--centered">
              <h3 class="predictive-search__item-heading h5">${esc(a.n)}</h3>
              <span class="predictive-search__item-meta">${a.c} program${a.c !== 1 ? 's' : ''}</span>
            </div>
          </a>
        </li>`;
      }
      html += '</ul>';
    }

    // Product section
    if (results.products.length) {
      html += '<h2 class="predictive-search__heading text-body caption-with-letter-spacing">Products</h2>';
      html += '<ul class="predictive-search__results-list list-unstyled" role="listbox" aria-labelledby="predictive-search-products">';
      for (const p of results.products) {
        idx++;
        const title = p.t.replace('Digital Audio Download', '').replace('Digital Download', '').trim();
        const imgHtml = p.i
          ? `<img class="predictive-search__image" src="${esc(p.i)}" alt="${esc(title)}" width="50" height="50">`
          : '';
        html += `<li id="predictive-search-option-${idx}" class="predictive-search__list-item" role="option" aria-selected="false">
          <a href="/products/${esc(p.h)}" class="predictive-search__item predictive-search__item--link link link--text" tabindex="-1">
            ${imgHtml}
            <div class="predictive-search__item-content">
              <h3 class="predictive-search__item-heading h5">${esc(title)}</h3>
              ${p.a ? `<span class="predictive-search__item-meta">by ${esc(p.a)}</span>` : ''}
              ${p.p ? `<span class="predictive-search__item-price">$${p.p}</span>` : ''}
            </div>
          </a>
        </li>`;
      }
      html += '</ul>';
    }

    // Article section
    if (results.articles && results.articles.length) {
      html += '<h2 class="predictive-search__heading text-body caption-with-letter-spacing">Articles</h2>';
      html += '<ul class="predictive-search__results-list list-unstyled" role="listbox" aria-labelledby="predictive-search-articles">';
      for (const art of results.articles) {
        idx++;
        const imgHtml = art.i
          ? `<img class="predictive-search__image" src="${esc(art.i)}" alt="${esc(art.t)}" width="50" height="50">`
          : '';
        html += `<li id="predictive-search-option-${idx}" class="predictive-search__list-item" role="option" aria-selected="false">
          <a href="/blogs/articles/${esc(art.h)}" class="predictive-search__item predictive-search__item--link link link--text" tabindex="-1">
            ${imgHtml}
            <div class="predictive-search__item-content">
              <h3 class="predictive-search__item-heading h5">${esc(art.t)}</h3>
              ${art.a ? `<span class="predictive-search__item-meta">by ${esc(art.a)}</span>` : ''}
            </div>
          </a>
        </li>`;
      }
      html += '</ul>';
    }

    // No results
    const articleCount = results.articles ? results.articles.length : 0;
    if (!results.products.length && !results.authors.length && !articleCount) {
      html += `<div class="predictive-search__no-results">
        <p class="predictive-search__no-results-text">No results found for &ldquo;${esc(searchTerm)}&rdquo;</p>
      </div>`;
    }

    // "Search for" button (always shown)
    html += `<ul class="predictive-search__results-list list-unstyled" role="listbox">
      <li id="predictive-search-option-search-keywords" class="predictive-search__list-item" role="option" aria-selected="false">
        <button class="predictive-search__item predictive-search__item--term link link--text h5 animate-arrow" tabindex="-1">
          Search for &ldquo;${esc(searchTerm)}&rdquo;
          <svg class="icon icon-arrow" viewBox="0 0 14 10" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path fill-rule="evenodd" clip-rule="evenodd" d="M8.537.808a.5.5 0 01.817-.162l4 4a.5.5 0 010 .708l-4 4a.5.5 0 11-.708-.708L11.793 5.5H1a.5.5 0 010-1h10.793L8.646 1.354a.5.5 0 01-.109-.546z" fill="currentColor"/>
          </svg>
        </button>
      </li>
    </ul>`;

    html += '</div>';

    // Accessibility live region
    const total = results.products.length + results.authors.length + (results.articles ? results.articles.length : 0);
    html += `<span class="hidden" data-predictive-search-live-region-count-value>${
      total === 0
        ? `No results found for "${esc(searchTerm)}"`
        : `${total} result${total !== 1 ? 's' : ''} found`
    }</span>`;

    return html;
  }

  /* ── Search dispatch ──────────────────────────────────── */

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(" ", "-").toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    // Client-side fuzzy search when index is ready
    if (this.searchIndex) {
      const results = this._fuzzySearch(searchTerm);
      const markup = this._buildResultsHTML(results, searchTerm);
      this.cachedResults[queryKey] = markup;
      this.renderSearchResults(markup);
      return;
    }

    // Fallback: Shopify Predictive Search API (while index loads)
    fetch(`${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&${encodeURIComponent('resources[type]')}=product&${encodeURIComponent('resources[limit]')}=4&section_id=predictive-search`)
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser().parseFromString(text, 'text/html').querySelector('#shopify-section-predictive-search').innerHTML;
        this.cachedResults[queryKey] = resultsMarkup;
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        this.close();
        throw error;
      });
  }

  /* ── UI state management ──────────────────────────────── */

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]').textContent);
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight = window.innerHeight - document.getElementById('shopify-section-header').getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }

    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false
    this.predictiveSearchResults.removeAttribute('style');

    this.isOpen = false;
  }
}

customElements.define('predictive-search', PredictiveSearch);
